import { type JSX, type ReactNode, useEffect, useState } from 'react';

import { type ConsumerOfferCard, type ConsumerPrice, earnPreview } from './api';
import { AppLink } from './router';

/**
 * The shared pieces of the discovery surfaces.
 *
 * ## Nothing here invents a signal
 *
 * If the BFF did not send a price, no price renders — not "£0.00", not "Free",
 * not "Price on request", which would imply a request could be made. If stock
 * is unknown, no stock badge renders; a marketplace that claims stock it cannot
 * see sells things it does not have. If a service has no bookability summary,
 * no availability line renders.
 *
 * That is the difference between a demo and a marketplace, and it is enforced
 * in these components rather than left to each page.
 */

/** Minor units to a readable amount. Display only — never fed back into maths. */
export function formatPrice(currency: string, amountMinor: number): string {
  const symbol = currency === 'GBP' ? '£' : currency === 'EUR' ? '€' : currency === 'USD' ? '$' : '';
  const whole = Math.trunc(Math.abs(amountMinor) / 100);
  const pence = String(Math.abs(amountMinor) % 100).padStart(2, '0');
  const sign = amountMinor < 0 ? '-' : '';

  return symbol === '' ? `${sign}${whole}.${pence} ${currency}` : `${sign}${symbol}${whole}.${pence}`;
}

/**
 * A price, or an honest absence.
 *
 * The compare-at and the saving are rendered only when the BFF sent them, and
 * the BFF sends them only when they are mathematically real. Nothing is
 * subtracted here: a second calculation could disagree with the number the
 * customer saw on the previous page.
 */
export function PriceTag({ price }: { readonly price: ConsumerPrice }): JSX.Element {
  if (!price.resolved || price.amountMinor === undefined || price.currency === undefined) {
    return (
      <p className="jt-price jt-price--unavailable">
        <span className="jt-price__amount">Price unavailable</span>
      </p>
    );
  }

  const amount = formatPrice(price.currency, price.amountMinor);

  return (
    <p className="jt-price">
      <span className="jt-price__amount">{amount}</span>

      {price.compareAtMinor !== undefined ? (
        <span className="jt-price__was">
          {/* Announced, not just struck through: a line through a number is a
              visual convention a screen reader does not convey. */}
          <span className="jt-visually-hidden">Was </span>
          <s>{formatPrice(price.currency, price.compareAtMinor)}</s>
        </span>
      ) : null}

      {price.savingMinor !== undefined ? (
        <span className="jt-price__saving">Save {formatPrice(price.currency, price.savingMinor)}</span>
      ) : null}
    </p>
  );
}

/** What a shopper needs to know before they commit to opening something. */
function RestrictionNote({
  restriction,
}: {
  readonly restriction: ConsumerOfferCard['restriction'];
}): JSX.Element | null {
  // Surfaced early on purpose. A shopper who reaches checkout before learning a
  // prescription is needed has been wasted, and hiding it looks deliberate.
  if (restriction.prescriptionRequired) {
    return <p className="jt-note jt-note--restriction">Prescription required</p>;
  }
  if (restriction.consultationRequired) {
    return <p className="jt-note jt-note--restriction">Consultation required</p>;
  }
  if (restriction.ageRestricted) {
    return <p className="jt-note jt-note--restriction">Age restricted</p>;
  }

  return null;
}

/** A service's availability, only when the diary actually answered. */
function BookabilityNote({
  bookability,
}: {
  readonly bookability: ConsumerOfferCard['bookability'];
}): JSX.Element | null {
  if (bookability === undefined) return null;

  if (!bookability.bookable) {
    // Shown rather than hidden: a customer looking for a clinic still wants to
    // find one that is fully booked this week, just not to be told it is free.
    return <p className="jt-note">No appointments available</p>;
  }

  return (
    <p className="jt-note jt-note--available">
      {bookability.nextAvailableAt === undefined
        ? 'Appointments available'
        : `Next appointment ${formatDate(bookability.nextAvailableAt)}`}
    </p>
  );
}

function formatDate(instant: string): string {
  const parsed = new Date(instant);
  if (Number.isNaN(parsed.getTime())) return '';

  return parsed.toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
}

/** "Earn X PENNY" — only when the server calculated a positive earn; nothing otherwise. */
export function EarnBadge({ units }: { readonly units: number | undefined }): JSX.Element | null {
  if (units === undefined || units <= 0) return null;
  return <p className="jt-note jt-note--earn">Earn {units} PENNY</p>;
}

/** Earn badges for a set of listings, in one request. Empty until (and unless) the server answers. */
export function useEarn(listingIds: readonly string[]): ReadonlyMap<string, number> {
  const [earn, setEarn] = useState<ReadonlyMap<string, number>>(new Map());
  const key = listingIds.join(',');
  useEffect(() => {
    const controller = new AbortController();
    void earnPreview(key === '' ? [] : key.split(','), controller.signal)
      .then(setEarn)
      .catch(() => undefined);
    return () => controller.abort();
  }, [key]);
  return earn;
}

export function OfferCard({
  offer,
  earnUnits,
}: {
  readonly offer: ConsumerOfferCard;
  readonly earnUnits?: number;
}): JSX.Element {
  return (
    <li className="jt-card">
      <article>
        <p className="jt-card__kind">{offer.kind === 'SERVICE' ? 'Service' : 'Product'}</p>

        <h3 className="jt-card__title">
          {/* The whole card is not a link: a card with three links inside it
              gives a screen reader three destinations for one result. */}
          <AppLink to={`/offers/${offer.id}`}>{offer.title}</AppLink>
        </h3>

        {offer.brandName !== undefined ? <p className="jt-card__brand">{offer.brandName}</p> : null}
        {offer.summary !== undefined ? <p className="jt-card__summary">{offer.summary}</p> : null}

        <p className="jt-card__seller">
          <AppLink to={`/storefronts/${offer.storefrontSlug}`}>{offer.storefrontName}</AppLink>
        </p>

        <PriceTag price={offer.price} />
        <EarnBadge units={earnUnits} />
        <RestrictionNote restriction={offer.restriction} />
        <BookabilityNote bookability={offer.bookability} />

        {/* Stock only when Phase 22 says something. `known: false` renders
            nothing rather than an invented "In stock". */}
        {offer.stock?.known === true ? (
          <p className="jt-note">{offer.stock.inStock === true ? 'In stock' : 'Out of stock'}</p>
        ) : null}

        {offer.fulfilmentModes.length > 0 ? (
          <ul className="jt-chips" aria-label="Ways to get this">
            {offer.fulfilmentModes.map((mode) => (
              <li key={mode} className="jt-chip">
                {fulfilmentLabel(mode)}
              </li>
            ))}
          </ul>
        ) : null}
      </article>
    </li>
  );
}

export function fulfilmentLabel(mode: string): string {
  switch (mode) {
    case 'COLLECTION':
      return 'Collection';
    case 'LOCAL_DELIVERY':
      return 'Local delivery';
    case 'NATIONAL_DELIVERY':
      return 'Delivery';
    case 'IN_PERSON_APPOINTMENT':
      return 'In person';
    case 'MOBILE_VISIT':
      return 'Mobile visit';
    case 'HOME_VISIT':
      return 'Home visit';
    case 'REMOTE_CONSULTATION':
      return 'Online';
    default:
      // An unknown mode renders as itself rather than being dropped: a silently
      // missing option is worse than an unfamiliar word.
      return mode;
  }
}

export function OfferGrid({
  offers,
  label,
}: {
  readonly offers: readonly ConsumerOfferCard[];
  readonly label: string;
}): JSX.Element {
  const earn = useEarn(offers.map((offer) => offer.id));
  return (
    <ul className="jt-grid" aria-label={label}>
      {offers.map((offer) => (
        <OfferCard key={offer.id} offer={offer} earnUnits={earn.get(offer.id)} />
      ))}
    </ul>
  );
}

/**
 * The three states every async surface has.
 *
 * `role="status"` on loading and empty so the change is announced; `role="alert"`
 * on the error so it interrupts. A spinner nobody is told about is a spinner
 * that leaves a screen-reader user on a silent page.
 */
export function LoadingState({ label }: { readonly label: string }): JSX.Element {
  return (
    <p className="jt-state jt-state--loading" role="status">
      {label}
    </p>
  );
}

export function EmptyState({
  title,
  children,
}: {
  readonly title: string;
  readonly children?: ReactNode;
}): JSX.Element {
  return (
    <div className="jt-state jt-state--empty" role="status">
      <p className="jt-state__title">{title}</p>
      {children}
    </div>
  );
}

export function ErrorState({
  message,
  onRetry,
}: {
  readonly message: string;
  readonly onRetry?: () => void;
}): JSX.Element {
  return (
    <div className="jt-state jt-state--error" role="alert">
      <p className="jt-state__title">{message}</p>
      {onRetry !== undefined ? (
        <button type="button" className="jt-button jt-button--secondary" onClick={onRetry}>
          Try again
        </button>
      ) : null}
    </div>
  );
}
